import { useMemo } from 'react';

import {
  useCreateAgentThread,
  useListAgentThreads,
  useDeleteAgentThread,
  usePatchThread,
  useGenerateAgentThreadTitle,
  ThreadDto
} from '@/api/generated';
import { classifyDate, DateCategory, isoToEpoch } from '@/utils/dateUtil';

const useThreads = () => {
  const {
    data: threadsResponse,
    isLoading: threadsLoading,
    isError: threadsError,
    refetch: refetchThreads
  } = useListAgentThreads();

  const threads = useMemo(() => threadsResponse?.items || [], [threadsResponse?.items]);

  const {
    mutate: createThread,
    mutateAsync: createThreadAsync,
    isPending: createThreadPending,
    isError: createThreadError
  } = useCreateAgentThread();

  const threadsByDateCategory = useMemo(() => {
    const categories = [
      DateCategory.Today,
      DateCategory.Yesterday,
      DateCategory.LastWeek,
      DateCategory.LastMonth
    ];

    const result = threads.reduce(
      (acc, thread) => {
        if (thread.createdAt) {
          const category = classifyDate(thread.createdAt, categories);
          return {
            ...acc,
            [category]: [...(acc[category] || []), thread]
          };
        }
        return acc;
      },
      {} as Record<string, ThreadDto[]>
    );

    return categories.map(category => ({
      category,
      threads: (result[category] || []).sort((a, b) => {
        if (!a.createdAt || !b.createdAt) {
          return 0;
        }
        const aEpoch = isoToEpoch(a.createdAt);
        const bEpoch = isoToEpoch(b.createdAt);
        return bEpoch - aEpoch;
      })
    }));
  }, [threads]);

  const {
    mutate: deleteThread,
    isPending: deleteThreadPending,
    isError: deleteThreadError
  } = useDeleteAgentThread();

  const {
    mutate: patchThread,
    isPending: patchThreadPending,
    isError: patchThreadError
  } = usePatchThread();

  const { mutateAsync: generateThreadTitleAsync } = useGenerateAgentThreadTitle();

  const flatThreads = useMemo(
    () =>
      threadsByDateCategory.flatMap(category => {
        if (category.threads.length === 0) return [];
        return [{ id: category.category, title: category.category }, ...category.threads];
      }),
    [threadsByDateCategory]
  );

  return {
    createThread,
    createThreadAsync,
    createThreadPending,
    createThreadError,
    threads,
    threadsByDateCategory,
    flatThreads,
    threadsLoading,
    threadsError,
    refetchThreads,
    deleteThread,
    deleteThreadPending,
    deleteThreadError,
    patchThread,
    patchThreadPending,
    patchThreadError,
    generateThreadTitleAsync
  };
};

export default useThreads;
